import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FlaskConical, Square } from "lucide-react";
import { ApiError } from "../api/client";
import { DEV_PERSONAS, useAuth, useRoles } from "../auth/AuthContext";
import { Roles } from "../auth/roles";
import { endsAtText, stopTestAs, testAsApi, useTestAsStatus } from "../lib/testAs/testAs";
import { useViewAs } from "../lib/viewAs/viewAs";

// The Development persona switcher, marked exactly as Summit Engineering marks its own stub sign-in. It is the only
// sign-in Summit Operations has until the Entra app registration exists (phase P1, blocked on Q-O8); the server owns
// the persona → role mapping and refuses this scheme outside Development.

export function PersonaSwitcher() {
  const { mode } = useAuth();
  return mode === "dev-persona" ? <DevPersonaSwitcher /> : <RoleSwitcher />;
}

function DevPersonaSwitcher() {
  const { persona, signInAsPersona } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const current = DEV_PERSONAS.find((p) => p.key === persona);

  return (
    <div className="relative flex-none">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="persona-switcher"
        onClick={() => setOpen((v) => !v)}
        title="Development persona sign-in. Microsoft Entra ID replaces this once the Summit Operations app registration exists (Q-O8)."
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
      >
        <FlaskConical aria-hidden="true" className="h-3 w-3" />
        <span className="hidden sm:inline">Development sign-in</span>
        <span className="font-semibold">{current?.label ?? "Choose"}</span>
        <ChevronDown aria-hidden="true" className={`h-3 w-3 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Development personas"
          className="absolute right-0 top-full z-40 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          <p className="px-3 py-2 text-[11px] leading-snug text-slate-500">
            Development sign-in only. The server decides the roles for each persona; Microsoft Entra ID replaces this in phase P1.
          </p>
          {DEV_PERSONAS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="menuitem"
              data-testid={`persona-${p.key}`}
              onClick={async () => {
                setOpen(false);
                const ok = await signInAsPersona(p.key);
                if (ok) navigate("/", { replace: true });
              }}
              className={`flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                p.key === persona ? "bg-brand-50" : ""
              }`}
            >
              <span className="text-sm font-medium text-slate-800">{p.label}</span>
              <span className="text-[11px] leading-snug text-slate-500">{p.capability}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// The order the owner asked for; the server decides which roles may be tested (TestAsRules.TestableRoles) and only those show.
const SWITCH_ORDER: string[] = [
  Roles.Purchasing,
  Roles.PurchasingManager,
  Roles.Logistics,
  Roles.ShippingManager,
  Roles.Planner,
  Roles.OperationsManager,
  Roles.Quality,
  Roles.Admin
];

/**
 * Entra mode: a real Admin switches role from the header through the existing server-side "Test as role" session
 * (authenticator step-up, 4 h limit, every act signed by the Admin). Full read/write as that role; the persistent banner
 * names the role and the end time, and "End" stops the session. Nothing here decides access: the server does.
 */
function RoleSwitcher() {
  const roles = useRoles();
  const viewAs = useViewAs();
  const eligible = roles.includes(Roles.Admin) && !viewAs;
  const status = useTestAsStatus(eligible);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!eligible || !status.data?.canTestAs) return null;
  const data = status.data;
  const active = data.active && data.roleLabel ? data : null;
  const rank = (role: string) => {
    const i = SWITCH_ORDER.indexOf(role);
    return i < 0 ? SWITCH_ORDER.length : i;
  };
  const options = [...data.roles].sort((a, b) => rank(a.role) - rank(b.role));
  // The header clips overflow, so the menu is a fixed-position portal anchored under the button.
  const anchor = buttonRef.current?.getBoundingClientRect();
  const place = { top: (anchor?.bottom ?? 48) + 4, right: Math.max(8, window.innerWidth - (anchor?.right ?? window.innerWidth - 8)) };

  async function start(role: string) {
    setBusy(role);
    setError(null);
    try {
      // One session at a time: switching stops the running one first (audited). A step-up 428 opens the authenticator prompt.
      if (active) await testAsApi.stop();
      await testAsApi.start(role);
      // Reload so the menu, the start page and every cached page follow the added role from a clean start.
      window.location.assign("/");
    } catch (e) {
      setBusy(null);
      setError(e instanceof ApiError ? e.message : "Switching role did not start. Try again.");
    }
  }

  return (
    <div className="relative flex flex-none items-center gap-1.5" data-testid="role-switch">
      {active && (
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-900"
          data-testid="role-switch-banner"
          role="status"
        >
          <FlaskConical aria-hidden="true" className="h-3 w-3" />
          <span>
            Testing as {active.roleLabel}
            {active.endsAtUtc ? ` · ends ${endsAtText(active.endsAtUtc)}` : ""}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 rounded border border-brand-300 bg-white px-1.5 font-semibold text-brand-900 hover:bg-brand-100 disabled:opacity-60"
            disabled={busy !== null}
            onClick={() => {
              setBusy("end");
              void stopTestAs();
            }}
            data-testid="role-switch-end"
          >
            <Square aria-hidden="true" className="h-2.5 w-2.5" /> End
          </button>
        </span>
      )}
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="role-switch-button"
        onClick={() => setOpen((v) => !v)}
        title="Test the app as another role, as yourself. Needs your authenticator code; ends after a few hours."
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-800 hover:bg-slate-50"
      >
        <FlaskConical aria-hidden="true" className="h-3 w-3" />
        <span>Switch role</span>
        <ChevronDown aria-hidden="true" className={`h-3 w-3 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
      </button>
      {open &&
        createPortal(
        <div ref={menuRef} role="menu" aria-label="Switch role" style={place} className="fixed z-50 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          <p className="px-3 py-2 text-[11px] leading-snug text-slate-500">
            Full read and write as that role. Every action is real and signed by you, recorded as "tested as". It ends after {data.maxHours} {data.maxHours === 1 ? "hour" : "hours"} or when you choose End.
          </p>
          {options.map((r) => (
            <button
              key={r.role}
              type="button"
              role="menuitem"
              disabled={busy !== null || active?.role === r.role}
              data-testid={`role-switch-${r.role}`}
              onClick={() => void start(r.role)}
              className={`flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60 ${active?.role === r.role ? "bg-brand-50" : ""}`}
            >
              <span>{r.label}</span>
              {active?.role === r.role && <span className="text-[11px] text-brand-700">now</span>}
              {busy === r.role && <span className="text-[11px] text-slate-500">Starting…</span>}
            </button>
          ))}
          {error && (
            <p role="alert" className="m-1 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800" data-testid="role-switch-error">
              {error}
            </p>
          )}
        </div>,
          document.body
        )}
    </div>
  );
}
